import { EVENT_BY_ID, UPGRADES, UPGRADE_BY_ID } from "./content";
import {
  REGION_IDS,
  NEWS_SCENES,
  RULES_VERSION,
  type CampaignState,
} from "./types";
import { FEATURE_STORY_IDS, NEWSROOM_LIMIT } from "./news";

/** Import preflight only: no normalization, rule execution, or state mutation. */
export class SaveShapeError extends Error {
  constructor(readonly path: string, expected: string) {
    super(`Invalid campaign save at ${path}: expected ${expected}`);
    this.name = "SaveShapeError";
  }
}

type RecordValue = Record<string, unknown>;
const SECTORS = ["consumer", "business", "government", "infrastructure"] as const;
const MAX_INTEGER = Number.MAX_SAFE_INTEGER;
// Leave headroom below JavaScript Date's ±100-million-day range for the start date.
const MAX_CALENDAR_TICK = 99_000_000;
const EVENT_CHOICES: Record<string, readonly string[]> = {
  E03: ["publish", "internal", "hold"],
  E04: ["admit", "dispute", "restrict", "conceal"],
  E05: ["fund", "slow", "continue"],
  E09: ["cooperate", "contest", "ignore"],
  E10: ["accept", "independent"],
};
const CONTINUOUS_FIELDS = ["trust", "coordination", "actualCapability", "perceivedCapability", "exposureRisk", "containment"] as const;
const REGION_CONTINUOUS_FIELDS = ["dependency", "alarm", "civilServices", "resistance", "machineReach", "refugeIntegrity", "independentServices", "facilityIntegrity"] as const;
const NUMERIC_PATHS = new Set<string>([
  ...CONTINUOUS_FIELDS,
  ...REGION_IDS.flatMap(id => [
    ...SECTORS.map(sector => `regions.${id}.sectors.${sector}`),
    ...REGION_CONTINUOUS_FIELDS.map(field => `regions.${id}.${field}`),
  ]),
]);

function fail(path: string, expected: string): never { throw new SaveShapeError(path, expected); }
function record(value: unknown, path: string): RecordValue {
  if (!value || typeof value !== "object" || Array.isArray(value)) fail(path, "an object");
  const prototype = Object.getPrototypeOf(value);
  if (prototype !== Object.prototype && prototype !== null) fail(path, "a plain JSON object");
  return value as RecordValue;
}
function list(value: unknown, path: string, maximum: number): unknown[] {
  if (!Array.isArray(value) || value.length > maximum) fail(path, `an array with at most ${maximum} entries`);
  return value;
}
function text(value: unknown, path: string, maximum = 128, allowEmpty = false): string {
  if (typeof value !== "string" || value.length > maximum || (!allowEmpty && value.length === 0)) {
    fail(path, `a ${allowEmpty ? "" : "nonempty "}string of at most ${maximum} characters`);
  }
  return value;
}
function number(value: unknown, path: string, minimum = 0, maximum = MAX_INTEGER, integer = false): number {
  if (typeof value !== "number" || !Number.isFinite(value) || value < minimum || value > maximum || (integer && !Number.isSafeInteger(value))) {
    fail(path, `${integer ? "an integer" : "a finite number"} between ${minimum} and ${maximum}`);
  }
  return value;
}
function boolean(value: unknown, path: string): void {
  if (typeof value !== "boolean") fail(path, "a boolean");
}
function choice(value: unknown, path: string, choices: readonly (string | number)[]): void {
  if (!choices.includes(value as string | number)) fail(path, `one of ${choices.join(", ")}`);
}
function nullableNumber(value: unknown, path: string, maximum = 100, integer = false): void {
  if (value !== null) number(value, path, 0, maximum, integer);
}
function unique(values: readonly unknown[], path: string): void {
  if (new Set(values).size !== values.length) fail(path, "unique entries");
}
function keysOnly(value: RecordValue, path: string, allowed: readonly string[]): void {
  for (const key of Object.keys(value)) if (!allowed.includes(key)) fail(`${path}.${key}`, "a supported field");
}
function knownUpgrade(value: unknown, path: string): string {
  const id = text(value, path);
  if (!Object.prototype.hasOwnProperty.call(UPGRADE_BY_ID, id)) fail(path, "a known upgrade ID");
  return id;
}
function upgradeList(value: unknown, path: string): void {
  const values = list(value, path, UPGRADES.length);
  values.forEach((item, index) => knownUpgrade(item, `${path}[${index}]`));
  unique(values, path);
}
function sectors(value: unknown, path: string): void {
  const object = record(value, path);
  keysOnly(object, path, SECTORS);
  SECTORS.forEach(sector => number(object[sector], `${path}.${sector}`, 0, 100));
}
function eventOccurrence(value: unknown, path: string, pending = false): string {
  const object = record(value, path);
  const id = text(object.occurrenceId, `${path}.occurrenceId`);
  const eventId = text(object.eventId, `${path}.eventId`);
  if (!Object.prototype.hasOwnProperty.call(EVENT_BY_ID, eventId)) fail(`${path}.eventId`, "a known event ID");
  number(object.triggeredTick, `${path}.triggeredTick`, 0, MAX_INTEGER, true);
  boolean(object.resolved, `${path}.resolved`);
  if (pending && (object.resolved !== false || !EVENT_BY_ID[eventId as keyof typeof EVENT_BY_ID].blocking)) {
    fail(path, "an unresolved blocking event");
  }
  if (object.optionId !== undefined) {
    choice(object.optionId, `${path}.optionId`, EVENT_CHOICES[eventId] ?? []);
    if (pending) fail(`${path}.optionId`, "no choice before resolution");
  }
  return id;
}
function rational(value: unknown, path: string): void {
  const object = record(value, path);
  const numerator = text(object.numerator, `${path}.numerator`, 128);
  const denominator = text(object.denominator, `${path}.denominator`, 128);
  if (!/^\d{1,128}$/.test(numerator)) fail(`${path}.numerator`, "a nonnegative integer string");
  if (!/^\d{1,128}$/.test(denominator) || /^0+$/.test(denominator)) fail(`${path}.denominator`, "a positive integer string");
}
function gameCommand(value: unknown, path: string): void {
  const object = record(value, path);
  switch (object.type) {
    case "purchase-upgrade": knownUpgrade(object.upgradeId, `${path}.upgradeId`); break;
    case "collect-opportunity": text(object.opportunityId, `${path}.opportunityId`); break;
    case "set-disclosure": choice(object.disclosure, `${path}.disclosure`, ["public", "internal"]); break;
    case "deploy-breakthrough":
    case "turn-hostile": break;
    case "set-region-operation":
      choice(object.regionId, `${path}.regionId`, REGION_IDS);
      if (object.operation !== null) choice(object.operation, `${path}.operation`, ["pressure", "protect", "reconnect"]);
      break;
    case "resolve-event":
      text(object.occurrenceId, `${path}.occurrenceId`);
      text(object.optionId, `${path}.optionId`);
      break;
    case "set-speed": choice(object.speed, `${path}.speed`, [0, 1, 2, 3]); break;
    case "set-paused": boolean(object.paused, `${path}.paused`); break;
    default: fail(`${path}.type`, "a supported gameplay command");
  }
}

/**
 * Reject unsafe or unresolvable imported shapes before pruneHistory,
 * normalizeNumerics, calculateResources, or any other engine operation runs.
 * Fixture prerequisite history is intentionally not checked here.
 * Numerics/replay/history are required; old pre-release shapes are unsupported.
 */
export function assertSaveShape(
  value: unknown,
): asserts value is CampaignState {
  const state = record(value, "campaign");
  choice(state.rulesVersion, "campaign.rulesVersion", [RULES_VERSION]);
  number(state.seed, "campaign.seed", 0, 0xffff_ffff, true);
  choice(state.startRegion, "campaign.startRegion", REGION_IDS);
  choice(state.phase, "campaign.phase", ["preparation", "hostile", "terminal"]);
  if (state.outcome !== null)
    choice(state.outcome, "campaign.outcome", [
      "human-extinction",
      "containment",
      "shutdown",
    ]);
  nullableNumber(state.outcomeTick, "campaign.outcomeTick", MAX_INTEGER, true);
  for (const field of [
    "tick",
    "revision",
    "cp",
    "hostileDays",
    "stageDwell",
    "shutdownDays",
    "heldBreakthroughs",
    "opportunityOccurrence",
    "commandSequence",
  ] as const) {
    number(
      state[field],
      `campaign.${field}`,
      0,
      field === "tick" ? MAX_CALENDAR_TICK : MAX_INTEGER,
      true,
    );
  }
  CONTINUOUS_FIELDS.forEach((field) =>
    number(state[field], `campaign.${field}`, 0, 100),
  );
  nullableNumber(state.frozenGlobalAdoption, "campaign.frozenGlobalAdoption");
  choice(state.requestedSpeed, "campaign.requestedSpeed", [0, 1, 2, 3]);
  choice(state.responseStage, "campaign.responseStage", [0, 1, 2, 3, 4, 5]);
  choice(state.rsiStage, "campaign.rsiStage", [0, 1, 2, 3]);
  choice(state.disclosure, "campaign.disclosure", ["public", "internal"]);
  boolean(state.playerPaused, "campaign.playerPaused");
  boolean(
    state.criticalDependencyAnnounced,
    "campaign.criticalDependencyAnnounced",
  );
  if (state.shutdownReason !== null)
    text(state.shutdownReason, "campaign.shutdownReason", 2000);

  const regions = record(state.regions, "campaign.regions");
  keysOnly(regions, "campaign.regions", REGION_IDS);
  for (const id of REGION_IDS) {
    const path = `campaign.regions.${id}`;
    const region = record(regions[id], path);
    choice(region.id, `${path}.id`, [id]);
    for (const field of ["active", "refuge", "extinctRewardClaimed"] as const)
      boolean(region[field], `${path}.${field}`);
    sectors(region.sectors, `${path}.sectors`);
    if (region.frozenSectors !== null)
      sectors(region.frozenSectors, `${path}.frozenSectors`);
    nullableNumber(region.frozenDependency, `${path}.frozenDependency`);
    REGION_CONTINUOUS_FIELDS.forEach((field) =>
      number(region[field], `${path}.${field}`, 0, 100),
    );
    if (region.operation !== null) choice(region.operation, `${path}.operation`, ["pressure", "protect", "reconnect"]);
    for (const field of ["nextCountermeasureDay", "countermeasureCount"])
      number(region[field], `${path}.${field}`, 0, MAX_INTEGER, true);
    const trends = record(region.trends, `${path}.trends`);
    const trendFields = ["civilServices", "resistance", "machineReach", "refugeIntegrity"];
    keysOnly(trends, `${path}.trends`, trendFields);
    trendFields.forEach(field => number(trends[field], `${path}.trends.${field}`, -100, 100));
    if (region.countermeasure !== null) {
      const c = record(region.countermeasure, `${path}.countermeasure`);
      keysOnly(c, `${path}.countermeasure`, ["kind", "announcedDay", "activatesDay", "endsDay"]);
      choice(c.kind, `${path}.countermeasure.kind`, ["disconnection", "facility-raids", "independent-recovery"]);
      for (const field of ["announcedDay", "activatesDay", "endsDay"])
        number(c[field], `${path}.countermeasure.${field}`, 0, MAX_INTEGER, true);
      if ((c.announcedDay as number) >= (c.activatesDay as number) || (c.activatesDay as number) >= (c.endsDay as number))
        fail(`${path}.countermeasure`, "an announcement before activation before expiry");
    }
    choice(region.policyTier, `${path}.policyTier`, [0, 1, 2, 3]);
    for (const field of [
      "initialPopulation",
      "livingPopulation",
      "refugeEntryPopulation",
    ] as const)
      number(region[field], `${path}.${field}`, 0, 8_000_000_000, true);
    number(
      region.casualtyResidual,
      `${path}.casualtyResidual`,
      0,
      999_999,
      true,
    );
  }

  upgradeList(state.purchasedUpgrades, "campaign.purchasedUpgrades");
  upgradeList(state.completedUpgrades, "campaign.completedUpgrades");
  const builds = list(state.builds, "campaign.builds", UPGRADES.length);
  const buildingIds = builds.map((value, index) => {
    const path = `campaign.builds[${index}]`;
    const build = record(value, path);
    const id = knownUpgrade(build.upgradeId, `${path}.upgradeId`);
    const definition = UPGRADE_BY_ID[id as keyof typeof UPGRADE_BY_ID];
    if (!definition.buildDays)
      fail(
        `${path}.upgradeId`,
        "an infrastructure upgrade with a build duration",
      );
    number(build.progressDays, `${path}.progressDays`, 0, definition.buildDays);
    return id;
  });
  unique(buildingIds, "campaign.builds");

  const opportunityIds = list(
    state.opportunities,
    "campaign.opportunities",
    1024,
  ).map((value, index) => {
    const path = `campaign.opportunities[${index}]`;
    const opportunity = record(value, path);
    const id = text(opportunity.id, `${path}.id`);
    choice(opportunity.regionId, `${path}.regionId`, REGION_IDS);
    choice(opportunity.category, `${path}.category`, [
      "blue",
      "purple",
      "gold",
    ]);
    for (const field of [
      "occurrence",
      "reward",
      "createdTick",
      "expiryTick",
    ] as const)
      number(
        opportunity[field],
        `${path}.${field}`,
        field === "occurrence" ? 1 : 0,
        MAX_INTEGER,
        true,
      );
    for (const field of ["protected", "claimed", "expired"] as const)
      boolean(opportunity[field], `${path}.${field}`);
    if (
      (opportunity.expiryTick as number) < (opportunity.createdTick as number)
    )
      fail(`${path}.expiryTick`, "a tick at or after creation");
    return id;
  });
  unique(opportunityIds, "campaign.opportunities");
  const triggered = list(state.triggeredEvents, "campaign.triggeredEvents", 12);
  triggered.forEach((id, index) => {
    const eventId = text(id, `campaign.triggeredEvents[${index}]`);
    if (!Object.prototype.hasOwnProperty.call(EVENT_BY_ID, eventId))
      fail(`campaign.triggeredEvents[${index}]`, "a known event ID");
  });
  unique(triggered, "campaign.triggeredEvents");
  unique(
    list(state.eventHistory, "campaign.eventHistory", 64).map((event, index) =>
      eventOccurrence(event, `campaign.eventHistory[${index}]`),
    ),
    "campaign.eventHistory",
  );
  if (state.pendingDecision !== null)
    eventOccurrence(state.pendingDecision, "campaign.pendingDecision", true);

  unique(
    list(state.timedModifiers, "campaign.timedModifiers", 128).map(
      (value, index) => {
        const path = `campaign.timedModifiers[${index}]`;
        const modifier = record(value, path);
        const id = text(modifier.id, `${path}.id`);
        choice(modifier.kind, `${path}.kind`, [
          "growth-rate",
          "containment-rate",
          "trust-target",
          "coordination-target",
          "alarm-target",
        ]);
        number(modifier.value, `${path}.value`, -1000, 1000);
        number(
          modifier.effectiveTick,
          `${path}.effectiveTick`,
          0,
          MAX_INTEGER,
          true,
        );
        number(modifier.expiryTick, `${path}.expiryTick`, 0, MAX_INTEGER, true);
        if (
          (modifier.expiryTick as number) <= (modifier.effectiveTick as number)
        )
          fail(`${path}.expiryTick`, "a tick after activation");
        return id;
      },
    ),
    "campaign.timedModifiers",
  );
  unique(
    list(state.scheduledEffects, "campaign.scheduledEffects", 128).map(
      (value, index) => {
        const path = `campaign.scheduledEffects[${index}]`;
        const effect = record(value, path);
        const id = text(effect.id, `${path}.id`);
        choice(effect.kind, `${path}.kind`, ["confirmed-exposure"]);
        number(effect.dueTick, `${path}.dueTick`, 0, MAX_INTEGER, true);
        return id;
      },
    ),
    "campaign.scheduledEffects",
  );
  const locks = record(state.purchaseLocks, "campaign.purchaseLocks");
  if (Object.keys(locks).length > UPGRADES.length)
    fail("campaign.purchaseLocks", "a bounded upgrade lock map");
  Object.entries(locks).forEach(([id, tick]) => {
    knownUpgrade(id, `campaign.purchaseLocks.${id}`);
    number(tick, `campaign.purchaseLocks.${id}`, 0, MAX_INTEGER, true);
  });
  list(state.canceledEffects, "campaign.canceledEffects", 256).forEach(
    (item, index) => text(item, `campaign.canceledEffects[${index}]`, 1000),
  );

  const milestones = record(state.milestones, "campaign.milestones");
  function thresholds(value: unknown, path: string, choices: number[]): void {
    const values = list(value, path, choices.length);
    values.forEach((item, index) => choice(item, `${path}[${index}]`, choices));
    unique(values, path);
  }
  thresholds(
    milestones.globalAdoption,
    "campaign.milestones.globalAdoption",
    [10, 20, 30, 40, 50, 60, 70, 80, 90],
  );
  thresholds(milestones.rsi, "campaign.milestones.rsi", [1, 2, 3]);
  thresholds(
    milestones.death,
    "campaign.milestones.death",
    [25, 50, 75, 90, 95],
  );
  const regionalClaims = record(
    milestones.regionalAdoption,
    "campaign.milestones.regionalAdoption",
  );
  keysOnly(regionalClaims, "campaign.milestones.regionalAdoption", REGION_IDS);
  REGION_IDS.forEach((id) =>
    thresholds(
      regionalClaims[id],
      `campaign.milestones.regionalAdoption.${id}`,
      [25, 50, 75],
    ),
  );

  const resources = record(state.resources, "campaign.resources");
  for (const field of [
    "availableCompute",
    "publicCompute",
    "privateCompute",
    "computeDemand",
    "nominalEnergySupply",
    "effectiveEnergySupply",
    "energyDemand",
    "nominalChipAccess",
    "effectiveChipAccess",
    "chipDemand",
  ] as const)
    number(resources[field], `campaign.resources.${field}`, 0, 1_000_000_000);
  number(resources.service, "campaign.resources.service", 0, 1);
  number(resources.humanSupport, "campaign.resources.humanSupport", 0, 1);
  choice(resources.limitingResource, "campaign.resources.limitingResource", [
    "compute",
    "energy",
    "chips",
    "none",
  ]);
  for (const field of ["exactService", "exactHumanSupport"] as const)
    rational(resources[field], `campaign.resources.${field}`);
  const takeoff = record(state.takeoff, "campaign.takeoff");
  choice(takeoff.label, "campaign.takeoff.label", [
    "stable",
    "accelerating",
    "rapid",
  ]);
  choice(takeoff.candidate, "campaign.takeoff.candidate", [
    "stable",
    "accelerating",
    "rapid",
  ]);
  number(
    takeoff.candidateDays,
    "campaign.takeoff.candidateDays",
    0,
    MAX_INTEGER,
    true,
  );
  list(takeoff.samples, "campaign.takeoff.samples", 30).forEach(
    (sample, index) =>
      number(sample, `campaign.takeoff.samples[${index}]`, 0, 100),
  );
  const rng = record(state.rng, "campaign.rng");
  keysOnly(rng, "campaign.rng", ["event", "opportunity", "exposure"]);
  ["event", "opportunity", "exposure"].forEach((field) =>
    number(rng[field], `campaign.rng.${field}`, 1, 0xffff_ffff, true),
  );
  unique(
    list(state.news, "campaign.news", 1024).map((value, index) => {
      const path = `campaign.news[${index}]`;
      const item = record(value, path);
      const id = text(item.id, `${path}.id`);
      number(item.tick, `${path}.tick`, 0, MAX_INTEGER, true);
      choice(item.kind, `${path}.kind`, [
        "news",
        "warning",
        "milestone",
        "terminal",
      ]);
      text(item.headline, `${path}.headline`, 2000);
      if (item.detail !== undefined)
        text(item.detail, `${path}.detail`, 8000, true);
      if (item.channel !== undefined)
        choice(item.channel, `${path}.channel`, ["world", "operations"]);
      if (item.report !== undefined) {
        const report = record(item.report, `${path}.report`);
        keysOnly(report, `${path}.report`, [
          "headline",
          "body",
          "source",
          "section",
          "scene",
          "regionId",
          "thread",
        ]);
        text(report.headline, `${path}.report.headline`, 2000);
        text(report.body, `${path}.report.body`, 8000);
        text(report.source, `${path}.report.source`, 200);
        text(report.section, `${path}.report.section`, 100);
        choice(report.scene, `${path}.report.scene`, NEWS_SCENES);
        if (report.regionId !== undefined)
          choice(report.regionId, `${path}.report.regionId`, REGION_IDS);
        if (report.thread !== undefined)
          text(report.thread, `${path}.report.thread`, 100);
      }
      return id;
    }),
    "campaign.news",
  );

  if (state.newsroom !== undefined) {
    const newsroom = record(state.newsroom, "campaign.newsroom");
    keysOnly(newsroom, "campaign.newsroom", [
      "published",
      "lastFeatureTick",
      "lastRegionId",
    ]);
    const published = list(
      newsroom.published,
      "campaign.newsroom.published",
      NEWSROOM_LIMIT,
    ).map((value, index) => {
      const key = text(value, `campaign.newsroom.published[${index}]`, 100);
      const [story, region] = key.split(":");
      if (
        !FEATURE_STORY_IDS.includes(story) ||
        !REGION_IDS.includes(region as (typeof REGION_IDS)[number]) ||
        key !== `${story}:${region}`
      )
        fail(
          `campaign.newsroom.published[${index}]`,
          "a supported story and region",
        );
      return story;
    });
    unique(published, "campaign.newsroom.published");
    number(
      newsroom.lastFeatureTick,
      "campaign.newsroom.lastFeatureTick",
      0,
      Number(state.tick),
      true,
    );
    if (newsroom.lastRegionId !== undefined)
      choice(
        newsroom.lastRegionId,
        "campaign.newsroom.lastRegionId",
        REGION_IDS,
      );
  }

  {
    const history = record(state.history, "campaign.history");
    const fields = [
      "newsSequence",
      "droppedNews",
      "retiredOpportunities",
      "collectedOpportunities",
      "missedOpportunities",
    ];
    keysOnly(history, "campaign.history", fields);
    fields.forEach((field) =>
      number(history[field], `campaign.history.${field}`, 0, MAX_INTEGER, true),
    );
  }
  {
    const numerics = record(state.numerics, "campaign.numerics");
    choice(numerics.version, "campaign.numerics.version", ["R01-EXACT-1"]);
    keysOnly(numerics, "campaign.numerics", ["version", "fields"]);
    const fields = record(numerics.fields, "campaign.numerics.fields");
    if (Object.keys(fields).length > NUMERIC_PATHS.size)
      fail("campaign.numerics.fields", "a bounded numeric field map");
    Object.entries(fields).forEach(([path, value]) => {
      if (!NUMERIC_PATHS.has(path))
        fail(
          `campaign.numerics.fields.${path}`,
          "a supported numeric field path",
        );
      const field = record(value, `campaign.numerics.fields.${path}`);
      number(
        field.basisPoints,
        `campaign.numerics.fields.${path}.basisPoints`,
        0,
        10_000,
        true,
      );
      number(
        field.residual,
        `campaign.numerics.fields.${path}.residual`,
        -999_999,
        999_999,
        true,
      );
    });
  }
  {
    const replay = record(state.replay, "campaign.replay");
    choice(replay.version, "campaign.replay.version", ["TCP-REPLAY-1"]);
    choice(
      replay.rulesVersion,
      "campaign.replay.rulesVersion",
      [RULES_VERSION],
    );
    choice(replay.numericVersion, "campaign.replay.numericVersion", [
      "R01-EXACT-1",
    ]);
    if (replay.newsVersion !== undefined)
      choice(replay.newsVersion, "campaign.replay.newsVersion", ["N01"]);
    number(replay.seed, "campaign.replay.seed", 0, 0xffff_ffff, true);
    choice(replay.startRegion, "campaign.replay.startRegion", REGION_IDS);
    boolean(replay.truncated, "campaign.replay.truncated");
    number(
      replay.firstRetainedSequence,
      "campaign.replay.firstRetainedSequence",
      1,
      MAX_INTEGER,
      true,
    );
    number(
      replay.totalAccepted,
      "campaign.replay.totalAccepted",
      0,
      MAX_INTEGER,
      true,
    );
    list(replay.entries, "campaign.replay.entries", 4096).forEach(
      (value, index) => {
        const path = `campaign.replay.entries[${index}]`;
        const entry = record(value, path);
        number(entry.sequence, `${path}.sequence`, 1, MAX_INTEGER, true);
        number(entry.tick, `${path}.tick`, 0, MAX_INTEGER, true);
        number(
          entry.baseRevision,
          `${path}.baseRevision`,
          0,
          MAX_INTEGER,
          true,
        );
        gameCommand(entry.command, `${path}.command`);
      },
    );
  }
}
